import React from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { Badge } from '@shared/components/ui/badge'
import { Button } from '@shared/components/ui/button'
import { Card, CardContent } from '@shared/components/ui/card'
import { Eye, CreditCard, Search, RefreshCw } from 'lucide-react'

interface Subscription {
  id: string
  user_email: string
  business_name: string
  plan_name?: string
  plan_monthly_price_cents?: number
  plan_annual_price_cents?: number
  billing_cycle: string
  status: string
  payment_status: string
  current_period_end?: string
}

interface SubscriptionTableProps {
  subscriptions: Subscription[]
  onViewSubscription?: (subscription: Subscription) => void
  onViewDetails?: (subscription: Subscription) => void
  loading?: boolean
  searchQuery?: string
  onRetry?: () => void
  error?: string
}

const SkeletonRow = () => (
  <TableRow>
    <TableCell>
      <div className="h-4 bg-muted rounded w-24 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-4 bg-muted rounded w-32 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-4 bg-muted rounded w-28 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="space-y-1">
        <div className="h-4 bg-muted rounded w-20 animate-pulse"></div>
        <div className="h-3 bg-muted rounded w-16 animate-pulse"></div>
      </div>
    </TableCell>
    <TableCell>
      <div className="h-6 bg-muted rounded w-16 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-6 bg-muted rounded w-16 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-4 bg-muted rounded w-24 animate-pulse"></div>
    </TableCell>
    <TableCell>
      <div className="h-8 bg-muted rounded w-16 animate-pulse"></div>
    </TableCell>
  </TableRow>
)

const LoadingState = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-center p-4">
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        <span className="text-muted-foreground">Loading subscriptions...</span>
      </div>
    </div>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Business</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead>Period End</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </TableBody>
    </Table>
  </div>
)

const EmptyState = ({ searchQuery, onRetry }: { searchQuery?: string; onRetry?: () => void }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center">
    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
      <CreditCard className="h-8 w-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-semibold mb-2">
      {searchQuery ? 'No subscriptions found' : 'No subscriptions yet'}
    </h3>
    <p className="text-muted-foreground max-w-sm mb-4">
      {searchQuery 
        ? 'Try adjusting your search criteria or filters to find what you\'re looking for.'
        : 'Subscriptions will appear here once they are created by platform users.'
      }
    </p>
    {onRetry && (
      <Button variant="outline" onClick={onRetry} className="flex items-center gap-2">
        <RefreshCw className="h-4 w-4" />
        Refresh
      </Button>
    )}
  </div>
)

const ErrorState = ({ error, onRetry }: { error: string; onRetry?: () => void }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center">
    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
      <Search className="h-8 w-8 text-destructive" />
    </div>
    <h3 className="text-lg font-semibold mb-2 text-destructive">Failed to load subscriptions</h3>
    <p className="text-muted-foreground max-w-sm mb-4">{error}</p>
    {onRetry && (
      <Button variant="outline" onClick={onRetry} className="flex items-center gap-2">
        <RefreshCw className="h-4 w-4" />
        Try Again
      </Button>
    )}
  </div>
)

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  subscriptions,
  onViewSubscription,
  onViewDetails,
  loading = false,
  searchQuery,
  onRetry,
  error
}) => {
  const handleView = onViewDetails || onViewSubscription

  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />
  }

  if (loading) {
    return <LoadingState />
  }

  if (subscriptions.length === 0) {
    return <EmptyState searchQuery={searchQuery} onRetry={onRetry} />
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Business</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead>Period End</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {subscriptions.map(sub => (
          <TableRow key={sub.id} className="hover:bg-muted/50 transition-colors">
            <TableCell className="font-mono text-sm">{sub.id}</TableCell>
            <TableCell className="font-medium">{sub.user_email}</TableCell>
            <TableCell>{sub.business_name}</TableCell>
            <TableCell>
              <div className="space-y-1">
                <div className="font-medium">{sub.plan_name || '—'}</div>
                <div className="text-sm text-muted-foreground">
                  {sub.billing_cycle && sub.plan_name ? (
                    (() => {
                      const cents = sub.billing_cycle === 'annual' 
                        ? sub.plan_annual_price_cents 
                        : sub.plan_monthly_price_cents;
                      return cents ? `$${(cents / 100).toFixed(2)} / ${sub.billing_cycle === 'annual' ? 'year' : 'month'}` : '—';
                    })()
                  ) : '—'}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <Badge 
                variant={sub.status === 'active' ? 'default' : sub.status === 'canceled' ? 'destructive' : 'secondary'}
                className="font-medium"
              >
                {sub.status}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge 
                variant={
                  sub.payment_status === 'active' ? 'default' : 
                  sub.payment_status === 'past_due' ? 'destructive' :
                  sub.payment_status === 'canceled' ? 'destructive' :
                  sub.payment_status === 'cancel_at_period_end' ? 'secondary' :
                  'secondary'
                }
                className="font-medium"
              >
                {sub.payment_status === 'active' ? 'Active' :
                 sub.payment_status === 'past_due' ? 'Past Due' :
                 sub.payment_status === 'canceled' ? 'Canceled' :
                 sub.payment_status === 'cancel_at_period_end' ? 'Canceling' :
                 sub.payment_status || 'Unknown'}
              </Badge>
            </TableCell>
            <TableCell className="text-sm">
              {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'}
            </TableCell>
            <TableCell>
              <div className="flex gap-2">
                {handleView && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleView(sub)}
                    className="flex items-center gap-1"
                    aria-label={`View details for subscription ${sub.id}`}
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default SubscriptionTable